import React, { useEffect, useState, useRef, useLayoutEffect, useMemo } from "react";
import {
    api, getZone, getRowsByZoneId, getSeatsOfZone, getSeatMapShapes, getSeatStatuses, getSeatOccupanciesOfEvent
} from "ticketino-api-client";
import {
    SEAT_RADIUS,
    SEAT_TEMPLATE_EXPANDED_WIDTH,
    SEAT_TEMPLATE_EXPANDED_HEIGHT,
    SEAT_TEMPLATE_EXPANDED_TEXT_MAXWIDTH,
    getSeatsBounds,
    getSeatTitle,
    transformSeats,
    translateSeats,
    SeatStatuses,
    getSeatStatusColor,
    getExpandedSeatTitle,
    getRectCoords,
} from "./seatMapSeatsUtils"
import {
    getId,
    idEq,
    idIn,
    idNotIn,
    last,
    objectMap,
    propNotEq,
} from "./functionalUtils"
import axios from 'axios';

import Loading from "../Loading";

const Zone = (props) => {

    //----variables----

    //developement env dependent
    const baseUrl = process.env.REACT_APP_BASE_TICKETINO_URL;
    const baseProxyUrl = process.env.REACT_APP_PLUGIN_PROXY;

    //prop values
    const selectedEventId = props.eventId;
    const selectedZoneId = props.zoneId;
    const selectedLanguage = props.languageISO;
    const token = props.token;
    const selectedSeatIds = props.selectedSeatIds;
    const onSeatClick = props.onSeatClick;
    const ticketTypes = props.ticketTypes;

    const [loading, setLoading] = useState(true);
    const [resources, setResources] = useState([]);
    const [selectedZone, setSelectedZone] = useState({});
    const [rows, setRows] = useState([]);
    const [seats, setSeats] = useState([]);
    const [seatmapShapes, setSeatmapShapes] = useState([]);
    const [seatStatuses, setSeatStatuses] = useState([]);
    const [mouseDownPos, setMouseDownPos] = useState(null)
    const [mouseMovePos, setMouseMovePos] = useState(null)
    const [requestSeatOccupancy, setRequestSeatOccupancy] = useState(false)

    const [zoneScale, setZoneScale] = useState(1)
    const [zoneX, setZoneX] = useState(0)
    const [zoneY, setZoneY] = useState(0)
    const zoneRef = useRef(null)


    useEffect(() => {
        requestResources(selectedLanguage);
    }, [selectedLanguage]);

    useEffect(() => {

        api.defaults.headers.common['Authorization'] = "Bearer " + token;
        api.defaults.baseURL = baseUrl;

        requestSelectedZone();

    }, []);

    useEffect(() => {

        if (requestSeatOccupancy === false) return;

        const interval = setInterval(async () => {
            try {
                var seatOccupancies = await getSeatOccupanciesOfEvent(selectedEventId);
                updateSeatOccupancies(seatOccupancies, seats);               // Then call update seat statuses
            } catch (error) {
            }
        }, 5000);

        return () => clearInterval(interval); // Cleanup on unmount
    }, [requestSeatOccupancy]);

    useEffect(() => {
        const { current } = zoneRef;

        if (!current) {
            return;
        }

        //prevent scroll when zooming in/out
        current.addEventListener('wheel', (e) => { e.preventDefault(); }, { passive: false });

    }, [selectedZone]);

    useEffect(() => {
        requestZoneDetails(selectedLanguage);
    }, [selectedZone]);

    const seatStatusValues = {
        EMPTY: 1,
        RESERVED: 2,
        SOLD: 3,
        RESERVED_BY_ORGANIZER: 4,
        OCCUPIED: 5,
        TABLE: 6,
        DONT_SHOW: 7,
        PLACEHOLDER: 8,
        RESERVED_BY_ORGANIZER_ME: 9,
        RESERVED_ME: 11,
    }

    const selectedSeats = useMemo(() => {
        const result = selectedSeatIds.map(id => seats.find(idEq(id)));
        return result;
    }, [selectedSeatIds, seats]);

    const unselectedSeats = useMemo(() => {
        const result = seats.filter(idNotIn(selectedSeatIds));
        return result;
    }, [selectedSeatIds, seats]);

    //request resources
    const requestSelectedZone = async (langauge) => {

        if (selectedZoneId > 0) {
            var zone = await getZone(selectedZoneId);
            setSelectedZone(zone);
        }
    };

    //request resources
    const requestResources = async (langauge) => {
        await axios
            .get(`${baseProxyUrl}/resources/${langauge}`)
            .then((res) => {
                setResources(res.data);
            })
            .catch((error) => console.error(error.response.data));
    };

    const requestZoneDetails = async (selectedLanguage) => {

        var seatmapShapes = await getSeatMapShapes();
        setSeatmapShapes(seatmapShapes);

        var seatStatuses = await getSeatStatuses(selectedLanguage);
        setSeatStatuses(seatStatuses);

        if (selectedZone && Object.keys(selectedZone).length > 0 && selectedZone.id > 0) {

            var rows = await getRowsByZoneId(selectedZone.id);
            setRows(rows);

            var seatsOfEvent = await getSeatsOfZone(selectedZone.id, selectedEventId);
            setSeats(seatsOfEvent);

            setRequestSeatOccupancy(true);

            resetZoneXYScale(seats, selectedZone);
        }
    }


    const updateSeatOccupancies = (seatOccupancies, currentSeats) => {
        if (!(currentSeats.length > 0)) return;

        const updatedSeats = currentSeats.map(seat => {
            const seatOccupancy = seatOccupancies.find(so => so.seatId === seat.id);

            return {
                ...seat,
                seatOccupancy: seatOccupancy ?? seat.seatOccupancy,
            };
        });

        setSeats(updatedSeats);
    };

    const resetZoneXYScale = (seats, zone) => {
        if (seats.length === 0) {
            setZoneX(0)
            setZoneY(0)
            setZoneScale(1)
            return
        }

        const stageCoords = zone?.stage.width ? getRectCoords(zone.stage) : []
        const backgroundImageCoords = zone?.backgroundImage?.url
            ? getRectCoords(zone.backgroundImage)
            : []
        const seatsBounds = getSeatsBounds(
            [...stageCoords, ...backgroundImageCoords, ...seats],
            SEAT_RADIUS + 10,
        )

        const seatsWidth = seatsBounds.right - seatsBounds.left
        const seatsHeight = seatsBounds.bottom - seatsBounds.top

        const zoneBounds = zoneRef.current.getBoundingClientRect()
        const zoneWidth = zoneBounds.right - zoneBounds.left
        const zoneHeight = zoneBounds.bottom - zoneBounds.top

        const scale = Math.min(zoneWidth / seatsWidth, zoneHeight / seatsHeight)

        setZoneX(-seatsBounds.left - seatsWidth / 2 + zoneWidth / 2 / scale)
        setZoneY(-seatsBounds.top - seatsHeight / 2 + zoneHeight / 2 / scale)
        setZoneScale(scale)
    }

    //const PieChart = ({ colors, r, ...props }) => {
    //    r /= 2
    //    const circumference = 2 * Math.PI * r
    //    const toCircUnit = p => (p / 100) * circumference
    //    const percent = 100 / colors.length

    //    return colors.map((color, i) => (
    //        <circle
    //            {...props}
    //            r={r}
    //            fill="transparent"
    //            stroke={color}
    //            strokeWidth={r * 2}
    //            strokeDasharray={`${toCircUnit(percent)} ${toCircUnit(
    //                100 - percent,
    //            )}`}
    //            strokeDashoffset={toCircUnit(25 + (100 - percent * i))}
    //        />
    //    ))
    //}

    const PieChart = ({ color, r, ...props }) => {
        r /= 2;
        return (
            <circle
                {...props}
                r={r}
                fill="transparent"
                stroke={color}
                strokeWidth={r * 2} // Make the stroke take up the full ring
            />
        );
    }



    const toZoneCoords = (clientX, clientY) => {
        const zoneBounds = zoneRef.current.getBoundingClientRect()
        return [
            (clientX - zoneBounds.left) / zoneScale - zoneX,
            (clientY - zoneBounds.top) / zoneScale - zoneY,
        ]
    }

    const handleMouseUp = event => {

        setMouseDownPos(null)
        setMouseMovePos(null)

        if (!mouseMovePos || !mouseDownPos) return
    }


    const scaleSeats = (scaleIn = false, x, y) => {
        const zoneBounds = zoneRef.current.getBoundingClientRect()
        x = x ?? (zoneBounds.left + zoneBounds.right) / 2
        y = y ?? (zoneBounds.top + zoneBounds.bottom) / 2
        const scalor = 1.1
        const newZoneScale = scaleIn ? zoneScale * scalor : zoneScale / scalor
        const [x1, y1] = toZoneCoords(x, y)
        const x2 = (x - zoneBounds.left) / newZoneScale
        const y2 = (y - zoneBounds.top) / newZoneScale

        setZoneScale(newZoneScale)
        setZoneX(x2 - x1)
        setZoneY(y2 - y1)
    }

    const mapSeat =
        (isSelected) =>
            ({ id, rowId, name, x, y, status, seatOccupancy, ticketTypeIds = [] }) => {

                let seatTicketTypes = [];

                ticketTypeIds.forEach(id => {
                    const ticketType = ticketTypes.find(tt => tt.id === id);

                    if (ticketType) {
                        seatTicketTypes.push(ticketType);
                    }
                });

                seatTicketTypes = seatTicketTypes.sort((a, b) => a.sortOrder - b.sortOrder);



                var seatStatus = seatOccupancy?.seatStatus ?? status;




                const seatStyle = {
                    fill: isSelected
                        ? "#ee7324"
                        : status === seatStatusValues.TABLE
                            ? "black"
                            : (seatStatus === 2
                                ? "url(#stripedPattern)"
                                : getSeatStatusColor(seatStatus))
                }

                const seatColor = seatTicketTypes?.slice()?.sort((a, b) => a.sortOrder - b.sortOrder)[0]?.colorCode || null;


                var seat = <React.Fragment key={id}>
                    <circle
                        cx={x}
                        cy={y}
                        r={SEAT_RADIUS}
                        style={{
                            cursor: seatStatus === seatStatusValues.EMPTY
                                ? "pointer"
                                : "not-allowed"
                        }}
                        onMouseDown={() => {
                            //if (isSelected) setIsDraggingSelection(true)
                        }}
                        onMouseUp={event => {
                            if (seatStatus === seatStatusValues.EMPTY) {
                                onSeatClick?.(event, {
                                    id,
                                    zoneName: selectedZone.zoneName,
                                    rowId,
                                    seatName: name,
                                    rowName: rows.find(r => r.id === rowId)?.name,
                                    status,
                                    seatTicketTypes
                                }, isSelected);
                            }
                        }}
                        {...(seatStyle)}
                    >
                        <title>
                            {getSeatTitle({
                                rowNameText: resources.seatmapPage?.Row,
                                seatText: resources.seatmapPage?.Seat,
                                statusText: resources.seatmapPage?.Status,
                                ticketTypesText: resources.seatmapPage?.TicketTypes,
                                seatStatuses,
                                rowId,
                                rows,
                                status: seatStatus,
                                name,
                                ticketTypes: seatTicketTypes,
                            })}
                        </title>
                    </circle>

                    {seatTicketTypes.length && seatStatus !== 3 && (
                        <PieChart
                            color={seatColor}
                            cx={x}
                            cy={y}
                            r={SEAT_RADIUS * 0.6}
                            style={{ pointerEvents: "none" }}
                        />
                    )}
                </React.Fragment>


                return seat
            }

    const selectedSeatElements = useMemo(
        () => selectedSeats.map(mapSeat(true)),
        [selectedSeats, resources],
    )

    const unselectedSeatElements = useMemo(
        () => unselectedSeats.map(mapSeat(false)),
        [unselectedSeats, resources],
    )


    return (
        <div>
            {!loading ? <Loading /> :
                <div style={{ height: "500px" }}>
                    {selectedZone && Object.keys(selectedZone).length > 0 && selectedZone.id > 0 &&
                        <div
                            id="zone"
                            className="flex-grow-1 row m-0 h-100"
                            ref={zoneRef}
                            style={{
                                overflow: "hidden",
                                cursor: "pointer",
                            }}
                            onDragStart={e => e.preventDefault()}
                            onMouseDown={({ clientX, clientY }) => {
                                const [x, y] = toZoneCoords(clientX, clientY)
                                setMouseDownPos({ x, y })
                            }}
                            onMouseMove={({ clientX, clientY }) => {
                                if (mouseDownPos === null) return

                                const [x, y] = toZoneCoords(clientX, clientY)
                                setMouseMovePos({ x, y })

                                setZoneX(x + zoneX - mouseDownPos.x)
                                setZoneY(y + zoneY - mouseDownPos.y)
                            }}
                            onMouseUp={handleMouseUp}
                            onMouseLeave={handleMouseUp}
                            onWheel={(event) => {
                                const { deltaY, clientX, clientY } = event;
                                scaleSeats(deltaY < 0, clientX, clientY);
                            }}
                        >
                            <svg
                                width="100%"
                                height="100%"
                                xmlns="http://www.w3.org/2000/svg"
                                xmlnsXlink="http://www.w3.org/1999/xlink" >
                                <defs>
                                    <pattern id="stripedPattern" patternUnits="userSpaceOnUse" width="6" height="6" patternTransform="rotate(45)">
                                        <rect width="3" height="6" fill="#ccc" />
                                    </pattern>
                                </defs>
                                <g transform={`translate(${zoneX * zoneScale} ${zoneY * zoneScale}) scale(${zoneScale} ${zoneScale})`}>
                                    {selectedZone?.backgroundImage.url && (
                                        <image
                                            x={selectedZone.backgroundImage.x - SEAT_RADIUS}
                                            y={selectedZone.backgroundImage.y - SEAT_RADIUS}
                                            width={selectedZone.backgroundImage.width}
                                            height={selectedZone.backgroundImage.height}
                                            href={selectedZone.backgroundImage.url}
                                            style={{ pointerEvents: "none" }}
                                        />
                                    )}

                                    {selectedZone?.stage.width && (
                                        <>
                                            <rect
                                                x={selectedZone.stage.x - SEAT_RADIUS}
                                                y={selectedZone.stage.y - SEAT_RADIUS}
                                                width={selectedZone.stage.width}
                                                height={selectedZone.stage.height}
                                                fill="#939da6"
                                            />
                                            <text
                                                x={
                                                    selectedZone.stage.x +
                                                    selectedZone.stage.width / 2 -
                                                    SEAT_RADIUS
                                                }
                                                y={
                                                    selectedZone.stage.y +
                                                    selectedZone.stage.height / 2 -
                                                    SEAT_RADIUS
                                                }
                                                dominantBaseline="middle"
                                                textAnchor="middle"
                                                fill="white"
                                                fontWeight="bold"
                                                style={{ userSelect: "none" }}
                                            >
                                                {selectedZone.stage.stageTitle}
                                            </text>
                                        </>
                                    )}
                                    {unselectedSeatElements}

                                    {selectedSeatElements}
                                </g>
                            </svg>
                        </div>
                    }
                </div>
            }
        </div >
    )

}

export default Zone;